import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Counter, CheckBox } from '../common';
import { pricingService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';
import { renderTextWithLinks } from '../../utils/linkify';

/**
 * #233: 서비스 선택 옵션(수량형/체크박스/카시트 분기) 공용 모듈.
 *
 * BookingSettings(프라이빗 이동)의 옵션 서브시스템을 회귀 없이 재사용하기 위해
 * 별도 파일로 분리한 self-contained 버전. 데이터 계약(variant/baseQty/optionsTotal/
 * option payload)·렌더 양식·요금 계산은 BookingSettings 와 100% 동일하게 맞춤.
 * (이동 화면은 기존 인라인 구현을 그대로 두어 무영향 — 투어 등 신규 화면만 사용)
 */

// unit 정적 매핑 — DeepL 오역('명'→名 / '개'→dog) 회피. BookingSettings KO_UNIT_I18N 과 동일.
const KO_UNIT_I18N = { '명': 'common.people', '개': 'common.unit_count', '대': 'common.unit_vehicle' };

// counter(number) 옵션 표시 순서 (BookingSettings counterRank 와 동일)
const counterRank = (opt) => {
  const code = (opt.code || '').toLowerCase();
  if (code.includes('passenger')) return 1;
  if (code.includes('luggage')) return 2;
  if (code === 'infant_carseat') return 3;
  if (code === 'junior_carseat') return 4;
  if (opt.group === 'carseat') return 3.5;
  return 100;
};

/**
 * 서비스 옵션 데이터 훅 — fetch + 정렬 + 수량 state 초기화 + DeepL 번역 +
 * 합계(optionsTotal) + 예약 payload(optionInputs) 를 한 번에 제공.
 */
export function useServiceOptionList(serviceType, tourId) {
  const { t } = useTranslation();
  const [serviceOpts, setServiceOpts] = useState([]);
  const [optionQty, setOptionQty] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // 투어는 상품마다 service_type='tour' 를 공유하므로 tourId 로 상품별 스코핑 (전 투어 노출 방지).
    pricingService.getServiceOptions(serviceType, tourId)
      .then((opts) => {
        if (cancelled) return;
        const sorted = (opts || []).filter((o) => !o.auto).sort((a, b) => {
          const aCheckbox = a.variant === 'checkbox' ? 1 : 0;
          const bCheckbox = b.variant === 'checkbox' ? 1 : 0;
          if (aCheckbox !== bCheckbox) return aCheckbox - bCheckbox;
          return counterRank(a) - counterRank(b);
        });
        setServiceOpts(sorted);
        const init = {};
        sorted.forEach((o) => {
          init[o.id] = o.variant === 'checkbox' ? false : (o.baseQty ?? o.defaultQty ?? 0);
        });
        setOptionQty(init);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [serviceType, tourId]);

  // name/extraLabel DeepL 배치 번역 (BookingSettings 와 동일 패턴)
  const optTextsBundle = useMemo(() => {
    const out = [];
    serviceOpts.forEach((o) => { out.push(o.name || '', o.extraLabel || ''); });
    return out;
  }, [serviceOpts]);
  const tOptTexts = useDeepLTranslated(optTextsBundle);
  const translateKoUnit = (u) => {
    if (!u) return '';
    const key = KO_UNIT_I18N[u];
    return key ? t(key) : u;
  };
  const tOptions = serviceOpts.map((o, i) => ({
    ...o,
    nameKo: o.name, // 카시트 그룹 판별은 번역 전 한글로 (영문에서 부모 누출 방지)
    name: tOptTexts[i * 2] || o.name,
    unit: translateKoUnit(o.unit),
    extraLabel: tOptTexts[i * 2 + 1] || o.extraLabel,
  }));

  const optionsTotal = serviceOpts.reduce((sum, opt) => {
    if (opt.variant === 'checkbox') return sum + (optionQty[opt.id] ? opt.price : 0);
    const qty = optionQty[opt.id] || 0;
    const baseQty = opt.baseQty ?? opt.base_qty ?? 0;
    return sum + opt.price * Math.max(0, qty - baseQty);
  }, 0);

  const optionInputs = serviceOpts
    .filter((o) => (o.variant === 'checkbox' ? optionQty[o.id] : (optionQty[o.id] || 0) > 0))
    .map((o) => ({ option_id: o.id, quantity: o.variant === 'checkbox' ? 1 : (optionQty[o.id] || 1) }));

  return { tOptions, optionQty, setOptionQty, optionsTotal, optionInputs, loading, hasOptions: serviceOpts.length > 0 };
}

/**
 * 선택 옵션 렌더 (수량형/체크박스/카시트 분기). BookingSettings 의 렌더 블록과 동일 양식.
 * props: tOptions(번역됨), optionQty, setOptionQty, money(useMoney 인스턴스)
 */
export default function ServiceOptionList({ tOptions, optionQty, setOptionQty, money }) {
  const { t } = useTranslation();
  const opts = tOptions || [];

  // 카시트 그룹 판별 — BookingSettings 와 동일 규칙.
  const koName = (o) => (o.nameKo || o.name || '').trim();
  const isCarseatParent = (o) =>
    o.carseat_parent === true || o.code === 'carseat' || o.code === 'child_seat' || koName(o) === '카시트';
  const isCarseatVariant = (o) =>
    o.group === 'carseat'
    || o.code === 'infant_carseat' || o.code === 'junior_carseat'
    || (koName(o).includes('카시트') && koName(o) !== '카시트');

  const carseatParent = opts.find(isCarseatParent) || null;
  const carseatVariants = opts.filter((o) => o !== carseatParent && isCarseatVariant(o));
  const carseatGrouped = carseatVariants.length > 0;
  const headerParent = carseatGrouped ? carseatParent : null;
  const others = opts.filter((o) => o !== headerParent && !carseatVariants.includes(o));

  const renderOptItem = (opt, nested = false) => (
    opt.variant === 'number' ? (
      <div key={opt.id} className={`flex flex-col gap-1.5 ${nested ? 'pl-4' : ''}`}>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1 flex-wrap min-w-0">
            <span className="text-2sm font-medium text-mono-900 break-keep break-words">{opt.name}</span>
            {opt.price > 0 && (
              <span className="text-2sm font-medium text-mono-500 shrink-0">{`( +${money(opt.price)} )`}</span>
            )}
          </span>
          {opt.extraLabel && (
            <span className="block text-[15px] sm:text-[13px] font-normal text-mono-600 mt-0.5 leading-relaxed break-keep break-words whitespace-pre-line">{renderTextWithLinks(opt.extraLabel)}</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <Counter
            value={optionQty[opt.id]}
            onChange={(v) => setOptionQty((prev) => ({ ...prev, [opt.id]: v }))}
            min={opt.min}
            max={(() => {
              // #267: max_qty 는 가격단계(tier) 수로 저장됨 → 단일단가(tier=1)면 1이라 카운터가 1에 갇힘.
              //   1 초과일 때만 실제 수량 cap 으로 신뢰. 그 외엔 아래 태그기반/무제한으로 폴백.
              if (typeof opt.max === 'number' && opt.max > 1 && opt.max > (opt.baseQty ?? 0)) return opt.max;
              const tag = `${opt.id || ''} ${opt.code || ''} ${opt.name || ''}`.toLowerCase();
              if (tag.includes('infant_carseat') || tag.includes('영아')) return 1;
              if (tag.includes('junior_carseat') || tag.includes('주니어')) return 2;
              if (tag.includes('carseat') || tag.includes('child_seat') || tag.includes('child seat') || tag.includes('카시트')) return 3;
              if (tag.includes('wheelchair') || tag.includes('휠체어')) return 1;
              if (tag.includes('passenger') || tag.includes('people') || tag.includes('인원')) return 6;
              return undefined;
            })()}
            unit={opt.unit}
          />
          {(() => {
            const qty = optionQty[opt.id];
            const isBase = opt.baseQty != null && qty <= opt.baseQty;
            const chargeAmount = opt.price * Math.max(0, qty - (opt.baseQty ?? 0));
            const chargeActive = !isBase && opt.price > 0 && qty > 0;
            // 표시 통일: 추가요금(>0) 있을 때만 ( +N원 ) — 기본수량/무료(+0)는 숨김
            if (chargeAmount <= 0) return null;
            return (
              <span className={`text-2sm font-medium ${chargeActive ? 'text-orange-500' : 'text-mono-500'}`}>
                {`( +${money(chargeAmount)} )`}
              </span>
            );
          })()}
        </div>
      </div>
    ) : (
      <div key={opt.id} className={nested ? 'pl-4' : 'flex flex-col gap-0.5'}>
        <CheckBox
          label={`${opt.name}${opt.auto ? ` (${t('booking.auto_applied')})` : ''}`}
          extraLabel={opt.extraLabel}
          charge={opt.price > 0 ? `( +${money(opt.price)} )` : null}
          chargeActive={!!optionQty[opt.id]}
          checked={!!optionQty[opt.id]}
          onChange={() => setOptionQty((prev) => ({ ...prev, [opt.id]: !prev[opt.id] }))}
        />
      </div>
    )
  );

  if (opts.length === 0) return null;

  return (
    <>
      {others.map((opt) => renderOptItem(opt))}
      {carseatGrouped && (
        <div className="flex flex-col gap-1.5">
          <div className="mt-2 min-w-0">
            <span className="flex items-center gap-1 flex-wrap min-w-0">
              <span className="text-2sm font-medium text-mono-900 break-keep break-words">{t('booking.carseat_group', '카시트 추가')}</span>
            </span>
            <span className="block text-xs font-normal text-mono-500 mt-0.5 leading-relaxed break-keep break-words whitespace-pre-line">
              {headerParent?.extraLabel || t('booking.carseat_law_notice', '도로교통법상 만 6세(72개월) 미만은 카시트 착용 의무')}
            </span>
          </div>
          {carseatVariants.map((opt) => renderOptItem(opt, true))}
        </div>
      )}
    </>
  );
}
